/**
 * State utilities for Comments component
 * Handles state management helpers and notification handling
 */

/**
 * Creates a message handler function for showing toast notifications
 * @param {Function} setToastMessage - State setter for toast message
 * @param {Function} setToastVariant - State setter for toast variant
 * @param {Function} setShowToast - State setter for showing toast
 * @returns {Function} Function to show messages
 */
export const createMessageHandler = (setToastMessage, setToastVariant, setShowToast) => {
  return (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };
};

/**
 * Handles new comment detection and notification
 * @param {Array} prevComments - Previous comments array
 * @param {Array} newComments - New comments array
 * @param {Function} setNewComments - State setter for new comments
 * @param {Function} playNotificationSound - Function to play notification sound
 * @param {Function} showMessage - Function to show message
 * @returns {Array} Updated comments array
 */
export const handleNewComments = (prevComments, newComments, setNewComments, playNotificationSound, showMessage) => {
  if (prevComments.length === 0) return newComments;

  const getAllCommentIds = (comments) => {
    let allIds = [];
    comments.forEach(comment => {
      allIds.push(comment.id);
      if (comment.replies && comment.replies.length > 0) {
        allIds = allIds.concat(getAllCommentIds(comment.replies));
      }
    });
    return allIds;
  };

  const currentCommentIds = new Set(getAllCommentIds(prevComments));
  const newAllCommentIds = getAllCommentIds(newComments);
  const newCommentIds = newAllCommentIds.filter(id => !currentCommentIds.has(id));

  if (newCommentIds.length > 0) {
    console.log('New comments detected:', newCommentIds);

    setNewComments(prev => new Set([...prev, ...newCommentIds]));
    playNotificationSound();

    if (newCommentIds.length === 1) {
      showMessage('New comment added!', 'success');
    } else {
      showMessage(`${newCommentIds.length} new comments added!`, 'success');
    }

    // Remove highlight after 15 seconds
    setTimeout(() => {
      setNewComments(prev => {
        const updated = new Set(prev);
        newCommentIds.forEach(id => updated.delete(id));
        return updated;
      });
    }, 15000);
  }

  return newComments;
};

/**
 * Handles upvote changes detection and notification
 * @param {Array} prevComments - Previous comments array
 * @param {Array} newComments - New comments array
 * @param {Function} setNewUpvotes - State setter for new upvotes
 * @param {Function} setTotalUpvoteCount - State setter for total upvote count
 * @param {Function} playUpvoteNotificationSound - Function to play upvote sound
 * @param {Function} showMessage - Function to show message
 */
export const handleUpvoteChanges = (prevComments, newComments, setNewUpvotes, setTotalUpvoteCount, playUpvoteNotificationSound, showMessage) => {
  const getAllComments = (comments) => {
    let allComments = [];
    comments.forEach(comment => {
      allComments.push(comment);
      if (comment.replies && comment.replies.length > 0) {
        allComments = allComments.concat(getAllComments(comment.replies));
      }
    });
    return allComments;
  };

  const oldAllComments = getAllComments(prevComments);
  const newAllComments = getAllComments(newComments);

  const upvoteChanges = newAllComments.filter(newComment => {
    const oldComment = oldAllComments.find(c => c.id === newComment.id);
    return oldComment && newComment.upvotes > oldComment.upvotes;
  });

  if (upvoteChanges.length > 0) {
    const totalUpvotesGained = upvoteChanges.reduce((total, newComment) => {
      const oldComment = oldAllComments.find(c => c.id === newComment.id);
      return total + (newComment.upvotes - oldComment.upvotes);
    }, 0);

    const upvotedCommentIds = upvoteChanges.map(c => c.id);
    setNewUpvotes(prev => {
      const newSet = new Set([...prev, ...upvotedCommentIds]);
      return newSet;
    });

    setTotalUpvoteCount(prev => prev + totalUpvotesGained);
    playUpvoteNotificationSound();

    if (upvoteChanges.length === 1) {
      showMessage('Comment upvoted!', 'info');
    } else {
      showMessage(`${upvoteChanges.length} comments upvoted!`, 'info');
    }

    // Remove highlight after 10 seconds
    setTimeout(() => {
      setNewUpvotes(prev => {
        const updated = new Set(prev);
        upvotedCommentIds.forEach(id => updated.delete(id));
        return updated;
      });
      setTotalUpvoteCount(0);
    }, 10000);
  }
};

/**
 * Updates comment upvotes in the comments array
 * @param {Array} comments - Current comments array
 * @param {number} commentId - ID of the comment to update
 * @param {number} newUpvotes - New upvote count
 * @returns {Array} Updated comments array
 */
export const updateCommentUpvotes = (comments, commentId, newUpvotes) => {
  return comments.map(comment => {
    if (comment.id === commentId) {
      return { ...comment, upvotes: newUpvotes };
    }
    if (comment.replies && comment.replies.length > 0) {
      return {
        ...comment,
        replies: updateCommentUpvotes(comment.replies, commentId, newUpvotes)
      };
    }
    return comment;
  });
};

/**
 * Removes a comment from the comments array
 * @param {Array} comments - Current comments array
 * @param {number} commentId - ID of the comment to remove
 * @returns {Array} Updated comments array with comment removed
 */
export const removeComment = (comments, commentId) => {
  return comments.filter(comment => {
    if (comment.id === commentId) {
      return false;
    }
    if (comment.replies && comment.replies.length > 0) {
      comment.replies = removeComment(comment.replies, commentId);
    }
    return true;
  });
};

/**
 * Creates a timeout handler for removing highlights
 * @param {Function} setState - State setter function
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Function to remove highlights after delay
 */
export const createHighlightRemover = (setState, delay) => {
  return (itemsToRemove) => {
    setTimeout(() => {
      setState(prev => {
        const updated = new Set(prev);
        itemsToRemove.forEach(item => updated.delete(item));
        return updated;
      });
    }, delay);
  };
};
